import React, { useState } from "react";
import { api, ApiError, type Tenancy } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export function EndTenancyForm({
  tenancy,
  onDone,
  onCancel,
}: {
  tenancy: Tenancy;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [exitDate, setExitDate] = useState(todayStr());
  const [deductions, setDeductions] = useState("0");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { t } = useLanguage();

  const deductionAmt = Math.max(0, Math.min(tenancy.deposit, Number(deductions) || 0));
  const refund = tenancy.deposit - deductionAmt;

  async function confirmEnd() {
    setBusy(true);
    try {
      await api.del(`/api/tenancies/${tenancy.id}`, { exitDate, deductions: deductionAmt, deductionNote: note });
      toast(t("endTenancyForm.tenancyEnded"));
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("endTenancyForm.couldNotEnd"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ background: "var(--surface-alt)", marginTop: 10 }}>
      <div className="sub" style={{ fontWeight: 800, marginBottom: 10 }}>{t("endTenancyForm.heading", { name: tenancy.name })}</div>

      <div className="field">
        <label>{t("endTenancyForm.dateOfExitLabel")}</label>
        <div className="input-wrap"><input type="date" value={exitDate} onChange={(e) => setExitDate(e.target.value)} /></div>
      </div>

      <div className="card-row">
        <span className="sub">{t("endTenancyForm.depositPaidLabel")}</span>
        <b>{fmtINR(tenancy.deposit)}</b>
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <label>{t("endTenancyForm.deductionsLabel")}</label>
        <div className="input-wrap">
          <input type="number" min={0} max={tenancy.deposit} value={deductions} onChange={(e) => setDeductions(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label>{t("endTenancyForm.deductionNoteLabel")}</label>
        <div className="input-wrap"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("endTenancyForm.deductionNotePlaceholder")} /></div>
      </div>

      <div className="card-row" style={{ marginTop: 4, paddingTop: 10, borderTop: "1px dashed var(--line)" }}>
        <span className="sub">{t("endTenancyForm.refundableDepositLabel")}</span>
        <b style={{ fontSize: 16 }}>{fmtINR(refund)}</b>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <button className="btn-solid btn-sm" style={{ flex: 1 }} disabled={busy} onClick={confirmEnd}>
          {busy ? t("endTenancyForm.endingBusy") : t("endTenancyForm.confirmButton")}
        </button>
      </div>
    </div>
  );
}
